var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { bringManipulativesToFront } from '../helper/helper';
import globalStore from '../store';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
import customEventHandler from '../helper/customEvents';
// Padding outset on each side so the bounding box doesn't hug the
// member sprites pixel-tight.
var PADDING = 12;
var ARROW_BASE = 10;
var ARROW_SHIFT = 50;
// When one of this group's OWN members is the active drag leader, that piece moves
// itself via direct DOM/local `position` state for perf and only commits its real
// `data.center` to React state on drop (see NumberAndMoneyPieceManipulative.tsx's
// stopDrag) — so `data.center` is STALE for that one member throughout the whole
// drag, while its siblings' centers update live via the central multi-select engine.
// Mixing one live + one stale center produced a bounding box whose width/height
// visibly drifted wrong every tick. `leaderOverride` substitutes the leader's live
// (start + cumulative delta) position in place of its stale `data.center`.
var computeBounds = function (members, leaderOverride) {
    var minX = Infinity;
    var minY = Infinity;
    var maxX = -Infinity;
    var maxY = -Infinity;
    members.forEach(function (m) {
        var _a, _b, _c;
        var d = m.data;
        var dims = (_b = (_a = d.dimensions) !== null && _a !== void 0 ? _a : d.defaultDimensions) !== null && _b !== void 0 ? _b : { width: 55, height: 55 };
        var scale = (_c = d.scale) !== null && _c !== void 0 ? _c : 1;
        var w = dims.width * scale;
        var h = dims.height * scale;
        var center = leaderOverride && leaderOverride.id === m.data.id ? leaderOverride.center : d.center;
        var left = center.x - w / 2;
        var right = center.x + w / 2;
        var top = center.y - h / 2;
        var bottom = center.y + h / 2;
        if (left < minX)
            minX = left;
        if (right > maxX)
            maxX = right;
        if (top < minY)
            minY = top;
        if (bottom > maxY)
            maxY = bottom;
    });
    if (!Number.isFinite(minX))
        return null;
    return {
        x: minX - PADDING,
        y: minY - PADDING,
        width: maxX - minX + PADDING * 2,
        height: maxY - minY + PADDING * 2,
    };
};
var NumberAndMoneyPieceSelectionGroup = function (_a) {
    var groupId = _a.groupId, members = _a.members;
    var t = useTranslation().t;
    var _b = useContext(DataContext), selectedTool = _b.selectedTool, setSelectedTool = _b.setSelectedTool, manipulative = _b.manipulative, setManipulative = _b.setManipulative, setContextMenu = _b.setContextMenu;
    var zoomLevel = globalStore.useStoreState(function (s) { return s.playerStore; }).zoomLevel;
    var isGroupActive = selectedTool.includes(groupId);
    var containerRef = useRef(null);
    // Broadcasts this group's drag as a multi-select leader so OTHER-type items (dice, tiles,
    // decks, ...) selected alongside it also follow. This component's own onMove/onUp below
    // only ever moves its OWN member pieces directly via setManipulative — it never told the
    // shared multi-select bus a drag was happening at all, so any other-type follower never
    // moved when an NMP group led the drag.
    var _c = useMultiSelectDragSource(groupId, selectedTool), startMultiDrag = _c.startMultiDrag, moveMultiDrag = _c.moveMultiDrag, endMultiDrag = _c.endMultiDrag;
    var memberIds = useMemo(function () { return members.map(function (m) { return m.data.id; }); }, [members]);
    // Live override for whichever member (if any) is the current drag leader — see
    // computeBounds's comment above for why this is needed (leader's own data.center
    // is stale mid-drag). Held in state (not just a ref) so setting it reliably
    // triggers a bounds recompute even on drags where no sibling's center changes.
    var _d = useState(null), leaderOverride = _d[0], setLeaderOverride = _d[1];
    var bounds = useMemo(function () { return computeBounds(members, leaderOverride); }, [members, leaderOverride]);
    // Refs let the keyboard / drag handlers read the freshest member list
    // without re-binding listeners every commit.
    var membersRef = useRef(members);
    membersRef.current = members;
    var manipulativeRef = useRef(manipulative);
    manipulativeRef.current = manipulative;
    // Member pieces are plain top-level manipulatives (not in SELF_MANAGED_TYPES), so
    // whenever this group's id is in `selectedTool` the central useMultiSelectFollowerEngine
    // (mounted once in GameController) already commits each member's real `center` via
    // setManipulative on every multiSelectMove tick — including when a different-type item
    // leads. `bounds` above is derived from those same live centers. The one exception is
    // when the drag LEADER is itself one of this group's own members — that piece skips
    // the central engine (see NumberAndMoneyPieceManipulative.tsx's onMouseDown) and moves
    // itself via direct DOM/local state, only committing `data.center` on drop. This effect
    // tracks that leader's live (start + cumulative delta) position via `leaderOverride` so
    // `computeBounds` can substitute it in place of the stale `data.center`.
    var leaderStartCenterRef = useRef(null);
    useEffect(function () {
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            var leaderMember = membersRef.current.find(function (m) { return m.data.id === sourceId; });
            leaderStartCenterRef.current = leaderMember
                ? { id: sourceId, center: __assign({}, leaderMember.data.center) }
                : null;
            if (leaderStartCenterRef.current)
                setLeaderOverride(leaderStartCenterRef.current);
        };
        var onMove = function (_a) {
            var sourceId = _a.sourceId, deltaX = _a.deltaX, deltaY = _a.deltaY;
            var leaderStart = leaderStartCenterRef.current;
            if (leaderStart && leaderStart.id === sourceId) {
                setLeaderOverride({
                    id: sourceId,
                    center: { x: leaderStart.center.x + deltaX, y: leaderStart.center.y + deltaY },
                });
            }
        };
        var onEnd = function () {
            leaderStartCenterRef.current = null;
            setLeaderOverride(null);
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        customEventHandler.addEventListener('multiSelectMoveEnd', onEnd);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
            customEventHandler.removeEventListener('multiSelectMoveEnd', onEnd);
        };
    }, [groupId, bounds]);
    // -- Group drag --------------------------------------------------------
    var dragRef = useRef(null);
    var beginDrag = function (e) {
        if (!isGroupActive)
            return;
        var isTouch = 'touches' in e;
        var clientX = isTouch ? e.touches[0].clientX : e.clientX;
        var clientY = isTouch ? e.touches[0].clientY : e.clientY;
        e.stopPropagation();
        var snapshot = {};
        membersRef.current.forEach(function (m) {
            var d = m.data;
            snapshot[m.data.id] = { x: d.center.x, y: d.center.y };
        });
        dragRef.current = { startX: clientX, startY: clientY, snapshot: snapshot };
        var moved = false;
        var onMove = function (ev) {
            if (!dragRef.current)
                return;
            var cx = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
            var cy = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
            var dx = (cx - dragRef.current.startX) / zoomLevel;
            var dy = (cy - dragRef.current.startY) / zoomLevel;
            if (!moved && Math.abs(dx) + Math.abs(dy) < 3)
                return;
            if (!moved) {
                moved = true;
                startMultiDrag();
            }
            var snap = dragRef.current.snapshot;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var sid = item.data.id;
                    if (!(sid in snap))
                        return item;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: snap[sid].x + dx, y: snap[sid].y + dy } }) });
                });
            });
            // Multi-select: broadcast delta so OTHER-type items follow
            moveMultiDrag(dx, dy);
        };
        var onUp = function () {
            var _a, _b;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            if (moved) {
                // Single batched undo entry for the whole group's move.
                var newCenters_1 = {};
                membersRef.current.forEach(function (m) {
                    var d = m.data;
                    newCenters_1[m.data.id] = { x: d.center.x, y: d.center.y };
                });
                var oldSnap = (_b = (_a = dragRef.current) === null || _a === void 0 ? void 0 : _a.snapshot) !== null && _b !== void 0 ? _b : {};
                var batchEntries = [
                    {
                        type: groupId,
                        id: groupId.split('_')[1],
                        propertyName: 'groupCenters',
                        old: JSON.stringify(oldSnap),
                        new: JSON.stringify(newCenters_1),
                    },
                ];
                // Bring the whole group to front on drop, mirroring individual NMP
                // pieces (see NumberAndMoneyPieceManipulative.tsx). Once a group is
                // active its overlay captures all drag input here, so this is the
                // only bring-to-front path for a group being re-dragged after the
                // initial click that selected it.
                var memberIdSet_1 = new Set(memberIds);
                var beforeIds = manipulativeRef.current.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                var orderedMemberIds_1 = manipulativeRef.current
                    .filter(function (m) { return memberIdSet_1.has(m.data.id); })
                    .map(function (m) { return m.data.id; });
                var alreadyAtFront = orderedMemberIds_1.length > 0 &&
                    beforeIds.slice(-orderedMemberIds_1.length).join(',') === orderedMemberIds_1.join(',');
                if (orderedMemberIds_1.length > 0 && !alreadyAtFront) {
                    // Functional update against live `prev` — member centers were
                    // committed continuously during onMove; replacing state with
                    // objects built from manipulativeRef.current here would risk
                    // clobbering a position update the ref hasn't caught up to.
                    var afterIds = __spreadArray(__spreadArray([], beforeIds.filter(function (bid) { return !memberIdSet_1.has(bid); }), true), orderedMemberIds_1, true);
                    setManipulative(function (prev) { return bringManipulativesToFront(prev, orderedMemberIds_1); });
                    batchEntries.push({
                        type: groupId,
                        id: groupId.split('_')[1],
                        propertyName: 'reorder',
                        old: JSON.stringify(beforeIds),
                        new: JSON.stringify(afterIds),
                    });
                }
                if (!registerMultiSelectSaveEntries(groupId, batchEntries)) {
                    saveState(batchEntries);
                }
                endMultiDrag();
            }
            dragRef.current = null;
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
    };
    // -- Arrow-key move ----------------------------------------------------
    var arrowMoveBatchRef = useRef(null);
    var flushArrowMove = function () {
        var batch = arrowMoveBatchRef.current;
        if (!batch)
            return;
        var newCenters = {};
        membersRef.current.forEach(function (m) {
            var d = m.data;
            newCenters[m.data.id] = { x: d.center.x, y: d.center.y };
        });
        saveState({
            type: groupId,
            id: groupId.split('_')[1],
            propertyName: 'groupCenters',
            old: JSON.stringify(batch.initial),
            new: JSON.stringify(newCenters),
        });
        arrowMoveBatchRef.current = null;
    };
    var moveByArrow = function (dx, dy) {
        if (!arrowMoveBatchRef.current) {
            var initial_1 = {};
            membersRef.current.forEach(function (m) {
                var d = m.data;
                initial_1[m.data.id] = { x: d.center.x, y: d.center.y };
            });
            arrowMoveBatchRef.current = { initial: initial_1, timer: null };
        }
        if (arrowMoveBatchRef.current.timer) {
            clearTimeout(arrowMoveBatchRef.current.timer);
        }
        arrowMoveBatchRef.current.timer = setTimeout(flushArrowMove, 500);
        var memberIdSet = new Set(memberIds);
        setManipulative(function (prev) {
            return prev.map(function (item) {
                var sid = item.data.id;
                if (!memberIdSet.has(sid))
                    return item;
                var c = item.data.center;
                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: c.x + dx, y: c.y + dy } }) });
            });
        });
    };
    var onKeyDown = function (e) {
        var _a;
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            e.stopPropagation();
            var rect = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect) {
                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: groupId });
            }
            return;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            flushArrowMove();
            e.currentTarget.blur();
            return;
        }
        var step = e.shiftKey ? ARROW_SHIFT : ARROW_BASE;
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            moveByArrow(step, 0);
        }
        else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            moveByArrow(-step, 0);
        }
        else if (e.key === 'ArrowDown') {
            e.preventDefault();
            moveByArrow(0, step);
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            moveByArrow(0, -step);
        }
    };
    // Scale is intentionally NOT handled here: GameController.tsx's own
    // "NMP group scale" bottomPanel case (search that file for the same
    // comment) already scales every same-type NMP piece and repositions
    // snapped clusters. A duplicate handler here previously caused NMP group
    // scale to jump by more than 0.1 per click, since both handlers fired
    // from the same dispatch and chained within one React batch.
    // -- onFocus: promote the group into selectedTool ----------------------
    var onFocus = function () {
        if (!selectedTool.includes(groupId))
            setSelectedTool([groupId]);
    };
    // -- onMouseDown: select then drag -------------------------------------
    var onMouseDown = function (e) {
        if (!selectedTool.includes(groupId))
            setSelectedTool([groupId]);
        beginDrag(e);
    };
    if (!bounds)
        return null;
    var ariaLabel = t('text.numberAndMoneyPieceGroupAriaLabel', {
        count: members.length,
        defaultValue: "Group of ".concat(members.length, " pieces"),
    });
    var wrapperStyle = {
        position: 'absolute',
        left: bounds.x,
        top: bounds.y,
        width: bounds.width,
        height: bounds.height,
        pointerEvents: isGroupActive ? 'auto' : 'none',
        cursor: isGroupActive ? 'grab' : 'default',
    };
    return (_jsx("div", { ref: containerRef, className: "numberAndMoneyPieceSelectionGroup".concat(isGroupActive ? ' active' : ''), "data-manipulative-id": groupId, "data-group-active": isGroupActive ? 'true' : 'false', tabIndex: 0, role: "region", "aria-label": ariaLabel, style: wrapperStyle, onFocus: onFocus, onKeyDown: onKeyDown, onMouseDown: onMouseDown, onTouchStart: onMouseDown }));
};
export default NumberAndMoneyPieceSelectionGroup;
